import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  configFileAtom,
  ingestedTreeAtom,
  ingestionContextAtom,
  partnerIngestionConfigAtom,
  sourceDataAtom,
} from './atoms'
import { useMemo } from 'react'
import {
  AttributeType,
  IngestionConfigItem,
  IngestionContext,
  NormalizedIngestionSource,
} from './types'
import { NodeType } from 'shared/types/ingestion'
import {
  ICourseNode,
  INodeList,
  ISimplifiedNode,
  SimplifiedNodeWithChildren,
} from '../../components/ingestion/IngestedTree'
import { createNodeList } from './nodeCreation'
import { isNodeListOfType } from '../../components/ingestion/TypeUtility'
import { camelToTitle } from '../../components/Helpers'
import { makeRegex, toTitleCase } from '../../utility'

export function useConfigBasedIngestion() {
  const configData = useAtomValue(configFileAtom)
  const sourceData = useAtomValue(sourceDataAtom)
  const partnerConfig = useAtomValue(partnerIngestionConfigAtom)
  const setIngestedTree = useSetAtom(ingestedTreeAtom)
  const ingestionContext = useAtomValue(ingestionContextAtom)
  const nodeParser = useParseNode()

  return useMemo(() => {
    const { canIngest, parseNode } = nodeParser
    return {
      canIngest: canIngest && !!sourceData,
      doIngest: () => {
        if (!sourceData) return
        const ingested = parseNode(sourceData)
        setIngestedTree(ingested as ICourseNode | undefined)
      },
    }
  }, [nodeParser, ingestionContext, configData, sourceData, partnerConfig])
}

export function useParseNode(config?: { skipChildren?: boolean }) {
  const skipChildren = !!config?.skipChildren
  const context = useAtomValue(ingestionContextAtom)
  return useMemo(() => {
    return {
      canIngest: !!context,
      parseNode: (node: NormalizedIngestionSource) => {
        if (!context) return
        return parseNode(node, { ...context, skipChildren }, 0)
      },
    }
  }, [context, skipChildren])
}

function parseNode(
  node: NormalizedIngestionSource,
  context: IngestionContext,
  nodeIdx: number,
) {
  const { type: sourceType, children, ...rest } = node
  const nodeConfig = context.config[sourceType]
  let nodeTypeErrors = context.ingestionErrors[sourceType]
  if (!nodeTypeErrors) {
    nodeTypeErrors = { attributes: {} }
    context.ingestionErrors[sourceType] = nodeTypeErrors
  }

  if (!nodeConfig) {
    nodeTypeErrors.unrecognized = true
    return
  }
  const { destinationType, customNodeTitle } = nodeConfig
  if (!destinationType) {
    nodeTypeErrors.noDestinationType = true
    return
  }
  const newNode = {
    type: destinationType,
    data: {
      title: titleForSourceData(rest, customNodeTitle, nodeIdx),
      ...context.dataForNode(destinationType as NodeType, node),
    },
    nodes: [],
  } as unknown as SimplifiedNodeWithChildren

  const { placedChildren, usedChildren } = parseNodeAttrs(
    newNode,
    nodeConfig,
    node,
    nodeIdx,
    context,
  )

  if (children?.length && !context.skipChildren) {
    const filteredChildren = children.filter((c) => !usedChildren.includes(c))
    doChildren(newNode, filteredChildren, context)
  }
  placedChildren.forEach(({ node, placement }) => {
    newNode.nodes.splice(placement, 0, node)
  })
  return newNode
}

function parseNodeAttrs(
  newNode: SimplifiedNodeWithChildren,
  config: IngestionConfigItem,
  sourceNode: NormalizedIngestionSource,
  parentIdx: number,
  context: IngestionContext,
) {
  const { usedAttrs, ...rest } = doTopLevelChildren(
    newNode,
    config,
    sourceNode,
    parentIdx,
    context,
  )
  config.attrIdOrder.forEach((attrName) => {
    if (usedAttrs.includes(attrName) || attrName === 'type') return
    const attrConfig = config.attributes[attrName]
    if (!attrConfig) {
      return
    }
    context.attributeIngestion(newNode, attrConfig, sourceNode, context)
  })
  return rest
}

function doTopLevelChildren(
  newNode: SimplifiedNodeWithChildren,
  config: IngestionConfigItem,
  sourceNode: NormalizedIngestionSource,
  parentIdx: number,
  context: IngestionContext,
) {
  const childNodes = config.childNodes ?? []
  return childNodes.reduce<{
    usedAttrs: string[]
    usedChildren: NormalizedIngestionSource[]
    placedChildren: { node: ISimplifiedNode; placement: number }[]
  }>(
    (acc, cn) => {
      const childNodes = cn.nodes.map((child) => {
        const { title, attrs } = child
        const childTitle = title.replaceAll('${idx}', String(parentIdx + 1))
        const childNode = {
          type: cn.childrenType,
          data: { title: childTitle },
          nodes: [],
        } as SimplifiedNodeWithChildren
        attrs.forEach(({ id, title }) => {
          acc.usedAttrs.push(id)
          const attrConfig = {
            ...config.attributes[id],
            customTitle: title,
          } as AttributeType
          context.attributeIngestion(childNode, attrConfig, sourceNode, context)
        })
        return childNode
      }) as ISimplifiedNode[]

      cn.filteredChildren?.forEach(({ titleLike }) => {
        const regTest = makeRegex(titleLike)
        const filteredSource = (sourceNode.children ?? []).filter((child) => {
          const childTitle = titleForSourceData(child, undefined, 0)
          return regTest.test(childTitle)
        })

        acc.usedChildren.push(...filteredSource)

        const filteredNodes = filteredSource.reduce<ISimplifiedNode[]>(
          (acc, n, i) => {
            const parsed = parseNode(n, context, i)
            if (parsed) acc.push(parsed)
            return acc
          },
          [],
        )

        childNodes.push(...filteredNodes)
      })

      if (!childNodes.length) return acc

      const nodeList: INodeList = createNodeList(
        { title: cn.listTitle },
        childNodes,
      )

      const placement = Number(cn.placement)
      if (!Number.isNaN(placement) && placement >= 0) {
        acc.placedChildren.push({ node: nodeList, placement })
      } else {
        newNode.nodes.push(nodeList)
      }

      return acc
    },
    { usedAttrs: [], usedChildren: [], placedChildren: [] },
  )
}

function doChildren(
  parentNode: SimplifiedNodeWithChildren,
  children: NormalizedIngestionSource[],
  context: IngestionContext,
) {
  children.forEach((child, idx) => {
    const config = context.config[child.type]
    const childNode = parseNode(child, context, idx)
    if (!childNode) {
      return
    }
    const listTitle =
      config.destinationTitle ?? camelToTitle(config.destinationType!)
    let nodeList = parentNode.nodes.find((n) => {
      return isNodeListOfType(n, childNode.type) && n.data.title === listTitle
    }) as INodeList | undefined

    if (!nodeList) {
      nodeList = createNodeList({ title: listTitle }, [])
      parentNode.nodes.push(nodeList)
    }
    nodeList.nodes.push(childNode)
  })
}

export function titleForSourceData(
  data: Record<string, unknown>,
  customTitle: string | undefined,
  idx: number,
) {
  if (!customTitle) {
    const title = data.title || data.name || ''
    return String(title)
  }
  const placeholders = extractPlaceholders(customTitle)
  return placeholders.reduce(
    (acc, ph) => {
      const replaceWith = toTitleCase(String(data[ph]))
      return acc.replaceAll('${' + ph + '}', replaceWith)
    },
    customTitle.replaceAll('${idx}', String(idx + 1)),
  )
}

function extractPlaceholders(str: string) {
  return [...str.matchAll(/\$\{([^}]+)}/g)].map((m) => m[1])
}
